import React, { useCallback, useEffect, useState } from 'react';
import './Modal.css';
import { IoCloseOutline, IoSearchOutline, IoDownloadOutline, IoOpenOutline, IoFileTrayOutline, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import { useLanguage } from '../../LanguageProvider';

export default function Modal({ onClose, fileUrl, fileName, mimeType, messageData, gallery, initialIndex = 0, simple = false }) {
    const { getTranslation } = useLanguage();
    const [isZoomed, setIsZoomed] = useState(false);
    const hasGallery = Array.isArray(gallery) && gallery.length > 0;
    const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);

    // Normalize current item for image mode
    const currentItem = hasGallery ? gallery[currentIndex] : { url: fileUrl, name: fileName, type: mimeType };

    const handleDownload = () => {
        const urlToOpen = hasGallery ? currentItem.url : fileUrl;
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = urlToOpen;
        link.download = hasGallery ? currentItem.name : fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleOpenInNewTab = () => {
        const urlToOpen = hasGallery ? currentItem.url : fileUrl;
        window.open(urlToOpen, '_blank', 'noopener,noreferrer');
    };

    const handleZoom = () => {
        setIsZoomed(!isZoomed);
    };

    const isImage = (hasGallery ? currentItem?.type : mimeType)?.startsWith('image/');
    const isPdf = (!hasGallery && mimeType === 'application/pdf');
    
    const handleContainerClick = (e) => {
        // Close only when clicking on the empty background area of the container.
        // Clicks on any children (image, chevrons, buttons, etc.) won't close.
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Check if file can be previewed
    const canPreview = (mimeType) => {
        if (!mimeType) return false;
        
        // Files that should NOT be previewed (security/safety reasons)
        const nonPreviewableTypes = [
            'text/html',
            'application/x-html',
            'application/xhtml+xml',
            'application/zip',
            'application/x-zip-compressed',
            'application/x-rar-compressed',
            'application/x-7z-compressed',
            'application/x-tar',
            'application/gzip',
            'application/x-executable',
            'application/x-msdownload',
            'application/x-shockwave-flash'
        ];
        
        // Check if file type should not be previewed
        if (nonPreviewableTypes.includes(mimeType)) {
            return false;
        }
        
        // Files that can be previewed
        const previewableTypes = [
            'image/',
            'video/',
            'audio/',
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'text/plain',
            'text/markdown',
            'text/css',
            'text/javascript',
            'application/json',
            'application/xml',
            'text/xml'
        ];
        
        return previewableTypes.some(type => mimeType.startsWith(type));
    };
    
    const fileCanBePreviewed = canPreview(mimeType);

    // Loading state management for all preview types
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // Reset loading/error states when fileUrl, mimeType, or gallery index changes
    useEffect(() => {
        setIsLoading(true);
        setHasError(false);
    }, [fileUrl, mimeType, currentIndex]);

    const goPrev = useCallback((e) => {
        if (e) e.stopPropagation();
        if (!hasGallery) return;
        setIsZoomed(false);
        setCurrentIndex((idx) => (idx - 1 + gallery.length) % gallery.length);
    }, [hasGallery, gallery?.length]);

    const goNext = useCallback((e) => {
        if (e) e.stopPropagation();
        if (!hasGallery) return;
        setIsZoomed(false);
        setCurrentIndex((idx) => (idx + 1) % gallery.length);
    }, [hasGallery, gallery?.length]);

    useEffect(() => {
        const onKey = (e) => {
            // Always allow ESC to close the preview (image, pdf, any file type)
            if (e.key === 'Escape') {
                onClose();
                return;
            }

            // Arrow navigation only makes sense when we have a gallery
            if (!hasGallery) return;
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [hasGallery, goNext, goPrev, onClose]);

    if (isImage) {
        return (
            <div className="modal-overlay file-preview">
                <div className="file-preview-container" onClick={handleContainerClick}>
                    {/* Top Left Info Bar - only show if not simple */}
                    {!simple && (
                        <div className="file-preview-info">
                            {messageData?.userAvatar && (
                                <img 
                                    src={messageData.userAvatar}
                                    alt={messageData.userName || 'User'}
                                    className="file-preview-user-avatar"
                                />
                            )}
                            <div className="file-preview-user-info">
                                <span className="file-preview-username">{messageData?.userName || 'User'}</span>
                                <span className="file-preview-timestamp">
                                    {messageData?.timestamp ? new Date(messageData.timestamp * 1000).toLocaleString() : ''}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Top Right Control Bar - only show if not simple */}
                    {!simple && (
                        <div className="file-preview-actions">
                            <button className="file-preview-action" title={isZoomed ? "Zoom out" : "Zoom in"} onClick={handleZoom}>
                                <IoSearchOutline size={16} />
                            </button>
                            <button className="file-preview-action" title={getTranslation("openInNewTab")} onClick={handleOpenInNewTab}>
                                <IoOpenOutline size={16} />
                            </button>
                            <button className="file-preview-action" title={getTranslation("downloadFile")} onClick={handleDownload}>
                                <IoDownloadOutline size={16} />
                            </button>
                            <button className="file-preview-action" title="Close" onClick={onClose}>
                                <IoCloseOutline size={16} />
                            </button>
                        </div>
                    )}

                    {/* Simple close button - only show if simple */}
                    {simple && (
                        <button className="file-preview-action close" title="Close" onClick={onClose}>
                            <IoCloseOutline size={16} />
                        </button>
                    )}

                    {/* Main Image */}
                    <div className={`file-preview-image-wrapper ${isZoomed ? 'zoomed' : ''}`}>
                        {isLoading && (
                            <div className="file-preview-loading">
                                <div className="file-preview-loading-indicator">
                                    <span><div /></span>
                                    <span><div /></span>
                                    <span><div /></span>
                                </div>
                            </div>
                        )}
                        {hasError && (
                            <div className="file-preview-no-preview">
                                <div className="file-preview-no-preview-message">
                                    {getTranslation("previewNotAvailable")}
                                </div>
                            </div>
                        )}
                        <img 
                            src={currentItem?.url} 
                            alt={currentItem?.name} 
                            className="file-preview-image"
                            style={{ display: isLoading || hasError ? 'none' : 'block' }}
                            onLoad={() => setIsLoading(false)}
                            onError={() => {
                                setIsLoading(false);
                                setHasError(true);
                            }}
                        />
                    </div>

                    {hasGallery && (
                        <>
                            <button
                                className="file-preview-nav file-preview-nav--prev"
                                onClick={(e) => goPrev(e)}
                                aria-label="Previous"
                                type="button"
                            >
                                <IoChevronBackOutline size={18} />
                            </button>
                            <button
                                className="file-preview-nav file-preview-nav--next"
                                onClick={(e) => goNext(e)}
                                aria-label="Next"
                                type="button"
                            >
                                <IoChevronForwardOutline size={18} />
                            </button>
                            <div className="file-preview-thumbnails">
                                {gallery.map((item, index) => (
                                    <div
                                        key={index}
                                        className={`file-preview-thumbnail ${index === currentIndex ? 'active' : ''}`}
                                        onClick={() => {
                                            setCurrentIndex(index);
                                            setIsZoomed(false);
                                        }}
                                    >
                                        <img src={item.url} alt={item.name} />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (isPdf) {
        return (
            <div className="modal-overlay file-preview">
                <div className="file-preview-container" onClick={handleContainerClick}>
                    <div className="file-preview-info">
                        {messageData?.userAvatar && (
                            <img
                                src={messageData.userAvatar}
                                alt={messageData.userName || 'User'}
                                className="file-preview-user-avatar"
                            />
                        )}
                        <div className="file-preview-user-info">
                            <span className="file-preview-username">{messageData?.userName || 'User'}</span>
                            <span className="file-preview-timestamp">
                                {messageData?.timestamp ? new Date(messageData.timestamp * 1000).toLocaleString() : ''}
                            </span>
                        </div>
                    </div>

                    <div className="file-preview-actions">
                        <button className="file-preview-action" title={getTranslation("openInNewTab")} onClick={handleOpenInNewTab}>
                            <IoOpenOutline size={16} />
                        </button>
                        <button className="file-preview-action" title={getTranslation("downloadFile")} onClick={handleDownload}>
                            <IoDownloadOutline size={16} />
                        </button>
                        <button className="file-preview-action" title="Close" onClick={onClose}>
                            <IoCloseOutline size={16} />
                        </button>
                    </div>

                    <div className="file-preview-pdf-wrapper">
                        {isLoading && (
                            <div className="file-preview-loading">
                                <div className="file-preview-loading-indicator">
                                    <span><div /></span>
                                    <span><div /></span>
                                    <span><div /></span>
                                </div>
                            </div>
                        )}
                        {hasError && (
                            <div className="file-preview-no-preview">
                                <div className="file-preview-no-preview-message">
                                    {getTranslation("previewNotAvailable")}
                                </div>
                            </div>
                        )}
                        <iframe
                            src={fileUrl}
                            title={fileName}
                            className="file-preview-pdf"
                            style={{ display: isLoading || hasError ? 'none' : 'block' }}
                            onLoad={() => setIsLoading(false)}
                            onError={() => {
                                setIsLoading(false);
                                setHasError(true);
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Fallback modal using same layout as image/PDF modals
    return (
        <div className="modal-overlay file-preview">
            <div className="file-preview-container" onClick={handleContainerClick}>
                {/* Top Left Info Bar */}
                <div className="file-preview-info">
                    {messageData?.userAvatar && (
                        <img 
                            src={messageData.userAvatar}
                            alt={messageData.userName || 'User'}
                            className="file-preview-user-avatar"
                        />
                    )}
                    <div className="file-preview-user-info">
                        <span className="file-preview-username">{messageData?.userName || 'User'}</span>
                        <span className="file-preview-timestamp">
                            {messageData?.timestamp ? new Date(messageData.timestamp * 1000).toLocaleString() : ''}
                        </span>
                    </div>
                </div>

                {/* Top Right Control Bar */}
                <div className="file-preview-actions">
                    <button className="file-preview-action" title={getTranslation("openInNewTab")} onClick={handleOpenInNewTab}>
                        <IoOpenOutline size={16} />
                    </button>
                    <button className="file-preview-action" title={getTranslation("downloadFile")} onClick={handleDownload}>
                        <IoDownloadOutline size={16} />
                    </button>
                    <button className="file-preview-action" title="Close" onClick={onClose}>
                        <IoCloseOutline size={16} />
                    </button>
                </div>

                {/* Main Content */}
                <div className="file-preview-content-wrapper">
                    {!fileCanBePreviewed ? (
                        <div className="file-preview-no-preview">
                            <div className="file-preview-no-preview-icon">
                                <IoFileTrayOutline size={64} />
                            </div>
                            <div className="file-preview-no-preview-message">
                                {getTranslation("previewNotAvailable")}
                            </div>
                            <div className="file-preview-no-preview-filename">
                                {fileName}
                            </div>
                        </div>
                    ) : mimeType?.startsWith('video/') ? (
                        <>
                            {isLoading && (
                                <div className="file-preview-loading">
                                    <div className="file-preview-loading-indicator">
                                        <span><div /></span>
                                        <span><div /></span>
                                        <span><div /></span>
                                    </div>
                                </div>
                            )}
                            {hasError && (
                                <div className="file-preview-no-preview">
                                    <div className="file-preview-no-preview-message">
                                        {getTranslation("previewNotAvailable")}
                                    </div>
                                </div>
                            )}
                            <video 
                                controls 
                                src={fileUrl} 
                                className="file-preview-video"
                                style={{ display: isLoading || hasError ? 'none' : 'block' }}
                                onLoadStart={() => setIsLoading(true)}
                                onCanPlay={() => setIsLoading(false)}
                                onError={() => {
                                    setIsLoading(false);
                                    setHasError(true);
                                }}
                            />
                        </>
                    ) : mimeType?.startsWith('audio/') ? (
                        <>
                            {isLoading && (
                                <div className="file-preview-loading">
                                    <div className="file-preview-loading-indicator">
                                        <span><div /></span>
                                        <span><div /></span>
                                        <span><div /></span>
                                    </div>
                                </div>
                            )}
                            {hasError && (
                                <div className="file-preview-no-preview">
                                    <div className="file-preview-no-preview-message">
                                        {getTranslation("previewNotAvailable")}
                                    </div>
                                </div>
                            )}
                            <audio 
                                controls 
                                src={fileUrl} 
                                className="file-preview-audio"
                                style={{ display: isLoading || hasError ? 'none' : 'block' }}
                                onLoadStart={() => setIsLoading(true)}
                                onCanPlay={() => setIsLoading(false)}
                                onError={() => {
                                    setIsLoading(false);
                                    setHasError(true);
                                }}
                            />
                        </>
                    ) : mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                        mimeType === 'application/vnd.ms-excel' ? (
                        <>
                            {isLoading && (
                                <div className="file-preview-loading">
                                    <div className="file-preview-loading-indicator">
                                        <span><div /></span>
                                        <span><div /></span>
                                        <span><div /></span>
                                    </div>
                                </div>
                            )}
                            {hasError && (
                                <div className="file-preview-no-preview">
                                    <div className="file-preview-no-preview-message">
                                        {getTranslation("previewNotAvailable")}
                                    </div>
                                </div>
                            )}
                            <iframe
                                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
                                className="file-preview-iframe"
                                frameBorder="0"
                                title={fileName}
                                style={{ display: isLoading || hasError ? 'none' : 'block' }}
                                onLoad={() => setIsLoading(false)}
                                onError={() => {
                                    setIsLoading(false);
                                    setHasError(true);
                                }}
                            />
                        </>
                    ) : mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                        mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ? (
                        <>
                            {isLoading && (
                                <div className="file-preview-loading">
                                    <div className="file-preview-loading-indicator">
                                        <span><div /></span>
                                        <span><div /></span>
                                        <span><div /></span>
                                    </div>
                                </div>
                            )}
                            {hasError && (
                                <div className="file-preview-no-preview">
                                    <div className="file-preview-no-preview-message">
                                        {getTranslation("previewNotAvailable")}
                                    </div>
                                </div>
                            )}
                            <iframe
                                src={`https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`}
                                className="file-preview-iframe"
                                frameBorder="0"
                                title={fileName}
                                style={{ display: isLoading || hasError ? 'none' : 'block' }}
                                onLoad={() => setIsLoading(false)}
                                onError={() => {
                                    setIsLoading(false);
                                    setHasError(true);
                                }}
                            />
                        </>
                    ) : (
                        <>
                            {isLoading && (
                                <div className="file-preview-loading">
                                    <div className="file-preview-loading-indicator">
                                        <span><div /></span>
                                        <span><div /></span>
                                        <span><div /></span>
                                    </div>
                                </div>
                            )}
                            {hasError && (
                                <div className="file-preview-no-preview">
                                    <div className="file-preview-no-preview-message">
                                        {getTranslation("previewNotAvailable")}
                                    </div>
                                </div>
                            )}
                            <iframe 
                                src={fileUrl} 
                                title={fileName} 
                                className="file-preview-iframe"
                                style={{ display: isLoading || hasError ? 'none' : 'block' }}
                                onLoad={() => setIsLoading(false)}
                                onError={() => {
                                    setIsLoading(false);
                                    setHasError(true);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}